import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session, User } from '@kmgestao/supabase-client';

import { supabase } from '../../lib/supabase.js';
import { identifyUser, resetPostHog } from '../../lib/posthog.js';
import { setSentryUser } from '../../lib/sentry.js';

export interface SessionState {
  /** `true` enquanto aguarda `getSession()` inicial. Use para evitar flicker em guards. */
  loading: boolean;
  /** Sessão ativa ou `null` se não autenticado. */
  session: Session | null;
  /** Atalho para `session?.user ?? null`. */
  user: User | null;
  /**
   * `aal` do JWT atual (Authenticator Assurance Level):
   * `aal1` = senha só · `aal2` = senha + MFA.
   * `null` enquanto loading.
   */
  aal: 'aal1' | 'aal2' | null;
}

const SessionContext = createContext<SessionState | null>(null);

/**
 * Provider que expõe a sessão atual do Supabase. Monta uma única vez no root
 * da aplicação (abaixo de QueryClientProvider). Subscreve em
 * `onAuthStateChange` para reagir a sign-in/sign-out em outras abas e renovação
 * automática de token.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    // Lê sessão persistida em localStorage (se houver)
    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setLoading(false);
      if (data.session?.user?.id) {
        setSentryUser(data.session.user.id);
        identifyUser(data.session.user.id);
      }
    });

    // Assina mudanças (login em outra aba, signOut, token refresh, recovery)
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setLoading(false);

      // Observabilidade: associa/desassocia user id (UUID) ao Sentry e PostHog.
      // NUNCA envia email/nome — só o id opaco do auth.users (LGPD §5.4).
      if (next?.user?.id) {
        setSentryUser(next.user.id);
        identifyUser(next.user.id);
      } else {
        setSentryUser(null);
        resetPostHog();
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<SessionState>(() => {
    const user = session?.user ?? null;
    const rawAal = session && readAalFromJwt(session.access_token);
    const aal: SessionState['aal'] = loading ? null : rawAal;
    return { loading, session, user, aal };
  }, [session, loading]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Hook de acesso à sessão. Lança erro se usado fora do `<SessionProvider>`.
 */
export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession() deve ser chamado dentro de <SessionProvider>.');
  }
  return ctx;
}

function readAalFromJwt(token: string): 'aal1' | 'aal2' | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      aal?: string;
    };
    return decoded.aal === 'aal2' ? 'aal2' : decoded.aal === 'aal1' ? 'aal1' : null;
  } catch {
    return null;
  }
}
